import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import toast from 'react-hot-toast';
import API from '../services/api';

function AdminDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const response = await API.get('/admin/stats');
      setStats(response.data.data);
    } catch (error) {
      console.error('Error loading stats:', error);
      toast.error('Failed to load dashboard stats');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-content">
          <div className="loading-spinner">⏳</div>
          <p className="loading-text">Loading admin dashboard...</p>
        </div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="dash-page text-center">
        <p className="text-danger">Failed to load dashboard data</p>
      </div>
    );
  }

  const usageData = [
    { name: 'AI Active',   value: stats.usage.ai_active,   color: '#3b82f6' },
    { name: 'Flow Active', value: stats.usage.flow_active,  color: '#6366f1' }
  ];

  const messagesChartData = stats.dailyMessages.map(item => ({
    date: new Date(item.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    count: parseInt(item.count)
  }));

  return (
    <div className="dash-page">
      <div className="dash-header">
        <h1 className="dash-title">👑 Super Admin Dashboard</h1>
        <p className="dash-subtitle">System-wide overview and analytics</p>
      </div>

      {/* Stat cards */}
      <div className="dash-stats-grid">
        <div onClick={() => navigate('/admin/clients')} className="dash-stat-card dash-stat-indigo cursor-pointer">
          <div className="dash-stat-label">Total Clients</div>
          <div className="dash-stat-value">{stats.overview.organizations.total}</div>
          <div className="dash-stat-sub">{stats.overview.organizations.active} active</div>
        </div>
        <div className="dash-stat-card dash-stat-violet">
          <div className="dash-stat-label">Total Users</div>
          <div className="dash-stat-value">{stats.overview.users.total}</div>
          <div className="dash-stat-sub">{stats.overview.users.today} new today</div>
        </div>
        <div className="dash-stat-card dash-stat-blue">
          <div className="dash-stat-label">Total Messages</div>
          <div className="dash-stat-value">{stats.overview.messages.total}</div>
          <div className="dash-stat-sub">{stats.overview.messages.today} today</div>
        </div>
        <div className="dash-stat-card dash-stat-sky">
          <div className="dash-stat-label">Total Flows</div>
          <div className="dash-stat-value">{stats.overview.flows.total}</div>
          <div className="dash-stat-sub">{stats.overview.flows.published} published</div>
        </div>
      </div>

      {/* Charts */}
      <div className="dash-charts-grid">
        <div className="dash-card">
          <h3 className="dash-card-title">🤖 AI vs Flow Usage</h3>
          {usageData[0].value + usageData[1].value > 0 ? (
            <div className="dash-chart-wrap">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={usageData}
                    cx="50%" cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    outerRadius={80}
                    dataKey="value"
                  >
                    {usageData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="empty-state-text">No data available</div>
          )}
          <div className="chart-legend">
            <div className="chart-legend-item">
              <div className="chart-legend-dot" style={{ background: '#3b82f6' }}></div>
              <span className="chart-legend-label">AI Agent</span>
            </div>
            <div className="chart-legend-item">
              <div className="chart-legend-dot" style={{ background: '#6366f1' }}></div>
              <span className="chart-legend-label">Flow Builder</span>
            </div>
          </div>
        </div>

        <div className="dash-card">
          <h3 className="dash-card-title">📊 Messages (Last 7 Days)</h3>
          {messagesChartData.length > 0 ? (
            <div className="dash-chart-wrap">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={messagesChartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="date" stroke="#94a3b8" fontSize={12} />
                  <YAxis stroke="#94a3b8" fontSize={12} />
                  <Tooltip contentStyle={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }} />
                  <Bar dataKey="count" fill="#3b82f6" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="empty-state-text">No message data</div>
          )}
        </div>
      </div>

      {/* Recent Clients */}
      <div className="dash-card">
        <div className="flex justify-between items-center mb-md">
          <h3 className="dash-card-title">🏢 Recent Clients</h3>
          <button onClick={() => navigate('/admin/clients')} className="btn btn-primary">
            View All →
          </button>
        </div>
        {stats.recentOrganizations && stats.recentOrganizations.length > 0 ? (
          <div className="flex flex-col gap-md">
            {stats.recentOrganizations.map(org => (
              <div
                key={org.id}
                onClick={() => navigate(`/admin/client/${org.id}`)}
                className="recent-client-item cursor-pointer"
              >
                <div>
                  <div className="recent-client-name">{org.name}</div>
                  <div className="recent-client-email">{org.email}</div>
                </div>
                <div className="recent-client-date">
                  {new Date(org.created_at).toLocaleDateString()}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="empty-state-text">No clients yet</p>
        )}
      </div>
    </div>
  );
}

export default AdminDashboard;